import { Button, IconButton, Tooltip } from '@assembly-js/design-system';
import { type CSSProperties, type ReactElement, useState } from 'react';

import { HELP_CENTER_PAGE } from 'src/constants';
import { PortalInterceptModal } from 'src/features/crm/portal-intercept-modal/PortalInterceptModal';
import { usePortalInterceptModal } from 'src/features/crm/portal-intercept-modal/usePortalInterceptModal';
import { usePortalHealthStatus } from 'src/hooks/usePortalHealthStatus';
import { UrlUtils } from 'src/utils';

import { useSidebarTheme } from './useSidebarTheme';

interface SidebarBottomBarProps {
  onSettingsClick: () => void;
  onHelpCenterClick: () => void;
  isClient: boolean;
  isHelpCenterOpen: boolean;
  isSettingsSelected: boolean;
}

const TOOLTIP_DELAY = 500;
const tooltipClassname = 'text-xs text-primary py-1 px-2 shadow-tooltip';
// Important fills beat DS secondary's resting white / near-white hover.
// Rest and hover must be mutually exclusive — both !important utilities exist
// in output.css and transparent wins the cascade if both are present.
const HOVER_FILL_CLASS = '!bg-secondary-hover';
const bottomBarButtonClassName =
  'rounded-full !bg-transparent transition-colors';
const bottomBarButtonHoverClassName = `rounded-full ${HOVER_FILL_CLASS} transition-colors`;
// Client Settings uses inline theme colors; avoid !bg-transparent so style wins.
const bottomBarClientButtonClassName = 'rounded-full transition-colors';

const useBottomBarHover = () => {
  const [hovered, setHovered] = useState(false);
  return {
    hovered,
    hoverProps: {
      onPointerEnter: () => setHovered(true),
      onPointerLeave: () => setHovered(false),
    },
    buttonClassName: hovered
      ? bottomBarButtonHoverClassName
      : bottomBarButtonClassName,
  };
};

const PortalButton = () => {
  const { isPortalReady, clientExperienceLink } = usePortalHealthStatus();
  const { shouldIntercept, isOpen, open, dismiss, goToCrm } =
    usePortalInterceptModal();
  const { hoverProps, buttonClassName } = useBottomBarHover();

  const openPortal = () =>
    window.open(UrlUtils.GetFullUrl(clientExperienceLink), '_blank');

  const handleClick = () => {
    // Before the Explore step is done, interrupt the shortcut and steer the
    // user back through the guided CRM flow rather than opening the portal cold.
    if (shouldIntercept) {
      open();
      return;
    }
    openPortal();
  };

  // "Continue to portal" honors the original shortcut but still records the
  // intercept as seen so it won't nag again.
  const handleContinueToPortal = () => {
    dismiss();
    openPortal();
  };

  return (
    <>
      <Tooltip
        content="Portal is getting setup. Check back in a minute"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        baseOffset={4}
        showDelayDuration={TOOLTIP_DELAY}
        tooltipClassname={tooltipClassname}
        disabled={isPortalReady}
      >
        <div {...hoverProps}>
          <Button
            label="Open Portal"
            suffixIcon="ArrowNE"
            variant="secondary"
            size="sm"
            className={buttonClassName}
            disabled={!isPortalReady}
            onClick={handleClick}
          />
        </div>
      </Tooltip>
      <PortalInterceptModal
        isOpen={isOpen}
        onClose={dismiss}
        onGoToCrm={goToCrm}
        onContinueToPortal={handleContinueToPortal}
      />
    </>
  );
};

export const SidebarBottomBar = ({
  onSettingsClick,
  onHelpCenterClick,
  isClient,
  isHelpCenterOpen,
  isSettingsSelected,
}: SidebarBottomBarProps): ReactElement => {
  const {
    sidebarTheme: { sidebarTextColor, sidebarItemHoverBackground },
  } = useSidebarTheme();
  const settingsHover = useBottomBarHover();
  const helpHover = useBottomBarHover();
  const clientSidebarStyles = {
    color: sidebarTextColor,
    borderColor: 'transparent',
    backgroundColor: settingsHover.hovered
      ? sidebarItemHoverBackground
      : 'transparent',
  } as CSSProperties;

  return (
    <div className="flex gap-2 px-1 py-1">
      <Tooltip
        content="Settings"
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        baseOffset={4}
        showDelayDuration={TOOLTIP_DELAY}
        tooltipClassname={tooltipClassname}
      >
        <div
          aria-current={isSettingsSelected ? 'page' : undefined}
          {...settingsHover.hoverProps}
        >
          <IconButton
            icon="Settings"
            aria-label="Settings"
            variant="secondary"
            size="sm"
            className={
              isClient
                ? bottomBarClientButtonClassName
                : settingsHover.buttonClassName
            }
            style={isClient ? clientSidebarStyles : undefined}
            onClick={onSettingsClick}
          />
        </div>
      </Tooltip>
      {!isClient && (
        <Tooltip
          content={HELP_CENTER_PAGE.label}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          baseOffset={4}
          showDelayDuration={TOOLTIP_DELAY}
          tooltipClassname={tooltipClassname}
        >
          <div aria-expanded={isHelpCenterOpen} {...helpHover.hoverProps}>
            <IconButton
              icon="QuestionMark"
              aria-label="Help Center"
              variant="secondary"
              size="sm"
              className={helpHover.buttonClassName}
              onClick={onHelpCenterClick}
            />
          </div>
        </Tooltip>
      )}
      {!isClient && (
        <div className="ml-auto">
          <PortalButton />
        </div>
      )}
    </div>
  );
};
